import React, { useState, useEffect } from 'react';

const BusinessIncomeForm = ({ data, updateData, nextStep, prevStep, incomeType, businessCategory }) => {
    const determinedBusinessType = incomeType === 'business_partnership' ? 'partnership' : 'proprietor';

    // Helpers for conditional rendering
    const isService = businessCategory === 'services';
    const isTrading = businessCategory === 'trading';
    const isManufacturing = businessCategory === 'manufacturing' || (!isService && !isTrading); // Default to manufacturing if unspecified

    const [formData, setFormData] = useState({
        businessType: data?.businessType || determinedBusinessType,
        grossTurnover: data?.grossTurnover || '',
        totalProfit: data?.totalProfit || '',

        // A. Business Operating Expenses
        rawMaterialPurchases: data?.rawMaterialPurchases || '',
        directLabourWages: data?.directLabourWages || '',
        manufacturingOverheads: data?.manufacturingOverheads || '',
        packagingMaterial: data?.packagingMaterial || '',
        freightCartageInward: data?.freightCartageInward || '',

        salariesWages: data?.salariesWages || '',
        employeeBenefits: data?.employeeBenefits || '',
        bonusesIncentives: data?.bonusesIncentives || '',
        staffWelfare: data?.staffWelfare || '',

        officeRent: data?.officeRent || '',
        factoryRent: data?.factoryRent || '',
        electricityPower: data?.electricityPower || '',
        waterCharges: data?.waterCharges || '',
        internetTelephone: data?.internetTelephone || '',
        officeMaintenance: data?.officeMaintenance || '',

        officeSupplies: data?.officeSupplies || '',
        printingStationery: data?.printingStationery || '',
        legalProfessionalFees: data?.legalProfessionalFees || '',
        bankCharges: data?.bankCharges || '',
        auditFees: data?.auditFees || '',
        subscriptionFees: data?.subscriptionFees || '',
        softwareLicenses: data?.softwareLicenses || '',

        advertisingPromotion: data?.advertisingPromotion || '',
        digitalMarketing: data?.digitalMarketing || '',
        salesCommission: data?.salesCommission || '',
        tradeShows: data?.tradeShows || '',
        samplesDemos: data?.samplesDemos || '',
        websiteMaintenance: data?.websiteMaintenance || '',

        interestBusinessLoans: data?.interestBusinessLoans || '',
        creditCardFees: data?.creditCardFees || '',
        loanProcessingCharges: data?.loanProcessingCharges || '',

        plantMachinery: data?.plantMachinery || '',
        furnitureFixtures: data?.furnitureFixtures || '',
        computersEquipment: data?.computersEquipment || '',
        vehicles: data?.vehicles || '',
        intangibleAssets: data?.intangibleAssets || '',

        gstPayments: data?.gstPayments || '',
        professionalTax: data?.professionalTax || '',
        tdsPayable: data?.tdsPayable || '',
        advanceTaxInstallments: data?.advanceTaxInstallments || '',

        travelConveyance: data?.travelConveyance || '',
        hotelBoarding: data?.hotelBoarding || '',
        entertainment: data?.entertainment || '',
        repairMaintenance: data?.repairMaintenance || '',
        insuranceBusinessAssets: data?.insuranceBusinessAssets || '',
        securityServices: data?.securityServices || '',

        // B. Investment & Growth
        newMachineryEquipment: data?.newMachineryEquipment || '',
        vehiclePurchase: data?.vehiclePurchase || '',
        officeExpansion: data?.officeExpansion || '',
        technologyUpgradation: data?.technologyUpgradation || '',

        marketResearch: data?.marketResearch || '',
        productDevelopment: data?.productDevelopment || '',
        trainingDevelopment: data?.trainingDevelopment || '',

        // Manufacturing Specific Fields
        dailyProduction: data?.dailyProduction || '',
        unitSellingPrice: data?.unitSellingPrice || '',
        workingCapitalInterest: data?.workingCapitalInterest || '',
        distributionTransport: data?.distributionTransport || '',
        // A. Direct
        powerFuel: data?.powerFuel || '',
        jobWorkCharges: data?.jobWorkCharges || '',
        consumableStores: data?.consumableStores || '',
        factoryRepairsMaintenance: data?.factoryRepairsMaintenance || '',

        // B. Factory
        plantMachineryMaintenance: data?.plantMachineryMaintenance || '',
        sparesTools: data?.sparesTools || '',
        qualityControl: data?.qualityControl || '',
        safetyCompliance: data?.safetyCompliance || '',
        pollutionControl: data?.pollutionControl || '',

        // D. Admin
        electricityWaterOffice: data?.electricityWaterOffice || '',

        // E. Marketing
        dealerCommission: data?.dealerCommission || '',

        // Other Income
        savingInterest: data?.savingInterest || '',
        fdInterest: data?.fdInterest || '',
        rdInterest: data?.rdInterest || '',
        licMaturity: data?.licMaturity || '',
        agriIncome: data?.agriIncome || '',

        // Partnership Fields
        partnershipTurnover: data?.partnershipTurnover || '',
        taxableProfit: data?.taxableProfit || '',
    });

    const [errors, setErrors] = useState({});
    const [expandedSections, setExpandedSections] = useState({
        cogs: false,
        employee: false,
        office: false,
        admin: false,
        marketing: false,
    });

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            businessType: determinedBusinessType
        }));
    }, [determinedBusinessType]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const validate = () => {
        const newErrors = {};

        if (formData.businessType === 'proprietor') {
            // Updated validation:
            // If it's Manufacturing, we are calculating profit based on inputs, so 'totalProfit' (the direct input field for non-mfg) is not mandatory.
            // But we should ensure at least Sales/Turnover is entered if we want some validation, or just let it pass.
            // Let's assume for Manufacturing detailed entry, direct 'totalProfit' check is skipped as it is not shown.

            if (!isManufacturing && !formData.totalProfit) {
                newErrors.totalProfit = 'Total Profit (Net Profit) is mandatory';
            }
        }

        if (formData.businessType === 'partnership') {
            if (!formData.taxableProfit) {
                newErrors.taxableProfit = 'Taxable Profit/Remuneration/Interest is mandatory';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            updateData(formData);
            nextStep();
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);

    return (
        <div className="form-step">
            <h2 className="step-title">
                {formData.businessType === 'partnership'
                    ? 'Partnership Income'
                    : `Business Income (${businessCategory ? businessCategory.charAt(0).toUpperCase() + businessCategory.slice(1) : 'Proprietor'})`}
            </h2>
            <p className="step-description">Please provide your business income and expense details</p>

            <form onSubmit={handleSubmit} className="diagnosis-form">

                {/* PROPRIETOR SECTION */}
                {formData.businessType === 'proprietor' && (
                    <>
                        {/* ---------------- MANUFACTURING LAYOUT ---------------- */}
                        {isManufacturing ? (
                            <>
                                {/* Income Section */}
                                <h3 className="section-heading">Income</h3>
                                <div className="grid-2-col">
                                    <InputGroup label="Sales / Turnover" name="grossTurnover" value={formData.grossTurnover} onChange={handleChange} />
                                    <InputGroup label="Other Income" name="otherIncomeAmount" value={formData.otherIncomeAmount} onChange={handleChange} />
                                    <InputGroup label="Daily Production (e.g. Litres)" name="dailyProduction" value={formData.dailyProduction} onChange={handleChange} />
                                    <InputGroup label="Selling Price per Unit (Avg)" name="unitSellingPrice" value={formData.unitSellingPrice} onChange={handleChange} />
                                </div>
                                <div className="total-display">
                                    Total Income: {formatCurrency((Number(formData.grossTurnover) || 0) + (Number(formData.otherIncomeAmount) || 0))}
                                </div>

                                {/* A. Direct / Production Expenses */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('cogs')}>
                                        A. Direct / Production Expenses
                                        <span className="toggle-icon">{expandedSections.cogs ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.cogs && (
                                        <>
                                            <div className="grid-2-col">
                                                <InputGroup label="Raw Material Purchases" name="rawMaterialPurchases" value={formData.rawMaterialPurchases} onChange={handleChange} />
                                                <InputGroup label="Direct Labour / Factory Wages" name="directLabourWages" value={formData.directLabourWages} onChange={handleChange} />
                                                <InputGroup label="Manufacturing Overheads" name="manufacturingOverheads" value={formData.manufacturingOverheads} onChange={handleChange} />
                                                <InputGroup label="Power & Fuel (Electricity/Cold Storage)" name="powerFuel" value={formData.powerFuel} onChange={handleChange} />
                                                <InputGroup label="Packing Material" name="packagingMaterial" value={formData.packagingMaterial} onChange={handleChange} />
                                                 <InputGroup label="Distribution & Transport" name="distributionTransport" value={formData.distributionTransport} onChange={handleChange} />
                                                <InputGroup label="Working Capital Interest" name="workingCapitalInterest" value={formData.workingCapitalInterest} onChange={handleChange} />
                                                <InputGroup label="Freight & Cartage Inward" name="freightCartageInward" value={formData.freightCartageInward} onChange={handleChange} />
                                                <InputGroup label="Job Work Charges" name="jobWorkCharges" value={formData.jobWorkCharges} onChange={handleChange} />
                                                <InputGroup label="Consumable Stores" name="consumableStores" value={formData.consumableStores} onChange={handleChange} />
                                                <InputGroup label="Factory Repairs & Maintenance" name="factoryRepairsMaintenance" value={formData.factoryRepairsMaintenance} onChange={handleChange} />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* B. Factory & Plant Expenses */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('factory')}>
                                        B. Factory & Plant Expenses
                                        <span className="toggle-icon">{expandedSections.factory ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.factory && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Factory Rent" name="factoryRent" value={formData.factoryRent} onChange={handleChange} />
                                            <InputGroup label="Plant & Machinery Maintenance" name="plantMachineryMaintenance" value={formData.plantMachineryMaintenance} onChange={handleChange} />
                                            <InputGroup label="Spares & Tools" name="sparesTools" value={formData.sparesTools} onChange={handleChange} />
                                            <InputGroup label="Quality Control Expenses" name="qualityControl" value={formData.qualityControl} onChange={handleChange} />
                                            <InputGroup label="Safety & Compliance" name="safetyCompliance" value={formData.safetyCompliance} onChange={handleChange} />
                                            <InputGroup label="Pollution Control Expenses" name="pollutionControl" value={formData.pollutionControl} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                                {/* C. Employee Costs */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('employee')}>
                                        C. Employee Costs
                                        <span className="toggle-icon">{expandedSections.employee ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.employee && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Salaries & Wages" name="salariesWages" value={formData.salariesWages} onChange={handleChange} />
                                            <InputGroup label="EPF, ESI, Labour Welfare" name="employeeBenefits" value={formData.employeeBenefits} onChange={handleChange} />
                                            <InputGroup label="Bonus & Incentives" name="bonusesIncentives" value={formData.bonusesIncentives} onChange={handleChange} />
                                            <InputGroup label="Staff Welfare" name="staffWelfare" value={formData.staffWelfare} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                                {/* D. Administrative & Office Expenses */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('admin')}>
                                        D. Administrative & Office Expenses
                                        <span className="toggle-icon">{expandedSections.admin ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.admin && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Office Rent" name="officeRent" value={formData.officeRent} onChange={handleChange} />
                                            <InputGroup label="Electricity & Water (Office)" name="electricityWaterOffice" value={formData.electricityWaterOffice} onChange={handleChange} />
                                            <InputGroup label="Internet & Telephone" name="internetTelephone" value={formData.internetTelephone} onChange={handleChange} />
                                            <InputGroup label="Printing & Stationery" name="printingStationery" value={formData.printingStationery} onChange={handleChange} />
                                            <InputGroup label="Legal & Professional Fees" name="legalProfessionalFees" value={formData.legalProfessionalFees} onChange={handleChange} />
                                            <InputGroup label="Audit Fees" name="auditFees" value={formData.auditFees} onChange={handleChange} />
                                            <InputGroup label="Software Licenses" name="softwareLicenses" value={formData.softwareLicenses} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                                {/* E. Marketing & Sales */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('marketing')}>
                                        E. Marketing & Sales
                                        <span className="toggle-icon">{expandedSections.marketing ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.marketing && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Advertising & Promotion" name="advertisingPromotion" value={formData.advertisingPromotion} onChange={handleChange} />
                                            <InputGroup label="Dealer Commission" name="dealerCommission" value={formData.dealerCommission} onChange={handleChange} />
                                            <InputGroup label="Trade Fairs & Exhibitions" name="tradeShows" value={formData.tradeShows} onChange={handleChange} />
                                            <InputGroup label="Samples & Demonstration" name="samplesDemos" value={formData.samplesDemos} onChange={handleChange} />
                                            <InputGroup label="Website & Digital Marketing" name="websiteMaintenance" value={formData.websiteMaintenance} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            /* ---------------- NON-MANUFACTURING LAYOUT (Existing/Default) ---------------- */
                            <>
                                {/* Basic Business Info */}
                                <h3 className="section-heading">Basic Business Information</h3>
                                <div className="grid-2-col">
                                    <InputGroup label="Gross Turnover (optional)" name="grossTurnover" value={formData.grossTurnover} onChange={handleChange} />
                                    <InputGroup label="Total Profit (Net Profit) *" name="totalProfit" value={formData.totalProfit} onChange={handleChange} error={errors.totalProfit} />
                                </div>

                                {/* A. BUSINESS OPERATING EXPENSES */}
                                <h3 className="section-heading" style={{ marginTop: '30px' }}>Business Operating Expenses</h3>

                                {/* 1. COGS (Hidden for Services) */}
                                {!isService && (
                                    <div className="expense-section">
                                        <h4 className="subsection-title collapsible" onClick={() => toggleSection('cogs')}>
                                            1. Cost of Goods Sold (COGS)
                                            <span className="toggle-icon">{expandedSections.cogs ? '▼' : '▶'}</span>
                                        </h4>
                                        {expandedSections.cogs && (
                                            <>
                                                <div className="grid-2-col">
                                                    <InputGroup
                                                        label={isTrading ? "Stock Purchases / Traded Goods" : "Raw Material Purchases"}
                                                        name="rawMaterialPurchases"
                                                        value={formData.rawMaterialPurchases}
                                                        onChange={handleChange}
                                                    />
                                                    {!isTrading && (
                                                        <InputGroup label="Direct Labour/Wages" name="directLabourWages" value={formData.directLabourWages} onChange={handleChange} />
                                                    )}
                                                    {!isTrading && (
                                                        <InputGroup label="Manufacturing Overheads" name="manufacturingOverheads" value={formData.manufacturingOverheads} onChange={handleChange} />
                                                    )}
                                                    <InputGroup label="Packaging Material" name="packagingMaterial" value={formData.packagingMaterial} onChange={handleChange} />
                                                </div>
                                                <InputGroup label="Freight & Cartage Inward" name="freightCartageInward" value={formData.freightCartageInward} onChange={handleChange} />
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* 2. Employee Costs */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('employee')}>
                                        2. Employee Costs
                                        <span className="toggle-icon">{expandedSections.employee ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.employee && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Salaries & Wages" name="salariesWages" value={formData.salariesWages} onChange={handleChange} />
                                            <InputGroup label="Employee Benefits (ESI, PF)" name="employeeBenefits" value={formData.employeeBenefits} onChange={handleChange} />
                                            <InputGroup label="Bonuses & Incentives" name="bonusesIncentives" value={formData.bonusesIncentives} onChange={handleChange} />
                                            <InputGroup label="Staff Welfare Expenses" name="staffWelfare" value={formData.staffWelfare} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                                {/* 3. Office/Rent Expenses */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('office')}>
                                        3. Office/Rent Expenses
                                        <span className="toggle-icon">{expandedSections.office ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.office && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Office Rent" name="officeRent" value={formData.officeRent} onChange={handleChange} />
                                            {!isService && (
                                                <InputGroup label="Factory/Workshop Rent" name="factoryRent" value={formData.factoryRent} onChange={handleChange} />
                                            )}
                                            <InputGroup label="Electricity & Power" name="electricityPower" value={formData.electricityPower} onChange={handleChange} />
                                            <InputGroup label="Water Charges" name="waterCharges" value={formData.waterCharges} onChange={handleChange} />
                                            <InputGroup label="Internet & Telephone" name="internetTelephone" value={formData.internetTelephone} onChange={handleChange} />
                                            <InputGroup label="Office Maintenance" name="officeMaintenance" value={formData.officeMaintenance} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                                {/* 4. Administration */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('admin')}>
                                        4. Administration Expenses
                                        <span className="toggle-icon">{expandedSections.admin ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.admin && (
                                        <>
                                            <div className="grid-2-col">
                                                <InputGroup label="Office Supplies & Stationery" name="officeSupplies" value={formData.officeSupplies} onChange={handleChange} />
                                                <InputGroup label="Printing & Stationery" name="printingStationery" value={formData.printingStationery} onChange={handleChange} />
                                                <InputGroup label="Legal & Professional Fees" name="legalProfessionalFees" value={formData.legalProfessionalFees} onChange={handleChange} />
                                                <InputGroup label="Bank Charges" name="bankCharges" value={formData.bankCharges} onChange={handleChange} />
                                                <InputGroup label="Audit Fees" name="auditFees" value={formData.auditFees} onChange={handleChange} />
                                                <InputGroup label="Subscription Fees" name="subscriptionFees" value={formData.subscriptionFees} onChange={handleChange} />
                                            </div>
                                            <InputGroup label="Software Licenses (ERP, Accounting)" name="softwareLicenses" value={formData.softwareLicenses} onChange={handleChange} />
                                        </>
                                    )}
                                </div>

                                {/* 5. Marketing & Sales */}
                                <div className="expense-section">
                                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('marketing')}>
                                        5. Marketing & Sales
                                        <span className="toggle-icon">{expandedSections.marketing ? '▼' : '▶'}</span>
                                    </h4>
                                    {expandedSections.marketing && (
                                        <div className="grid-2-col">
                                            <InputGroup label="Advertising & Promotion" name="advertisingPromotion" value={formData.advertisingPromotion} onChange={handleChange} />
                                            <InputGroup label="Digital Marketing Expenses" name="digitalMarketing" value={formData.digitalMarketing} onChange={handleChange} />
                                            <InputGroup label="Sales Commission" name="salesCommission" value={formData.salesCommission} onChange={handleChange} />
                                            <InputGroup label="Trade Shows & Exhibitions" name="tradeShows" value={formData.tradeShows} onChange={handleChange} />
                                            <InputGroup label="Samples & Demos" name="samplesDemos" value={formData.samplesDemos} onChange={handleChange} />
                                            <InputGroup label="Website Maintenance" name="websiteMaintenance" value={formData.websiteMaintenance} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>

                            </>
                        )}
                    </>
                )}

                {/* PARTNERSHIP SECTION */}
                {formData.businessType === 'partnership' && (
                    <>
                        <h3 className="section-heading">Partnership Income Details</h3>
                        <div className="grid-2-col">
                            <InputGroup label="Total Turnover (optional)" name="partnershipTurnover" value={formData.partnershipTurnover} onChange={handleChange} />
                            <InputGroup label="Taxable Profit / Remuneration / Interest *" name="taxableProfit" value={formData.taxableProfit} onChange={handleChange} error={errors.taxableProfit} />
                        </div>
                    </>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button type="button" onClick={() => { updateData(formData); prevStep(); }} className="btn-secondary">← Back</button>
                    <button type="submit" className="btn-primary">Next: More Expenses →</button>
                </div>
            </form>

            <style jsx>{`
                .section-heading {
                    color: #1f2937;
                    margin-bottom: 15px;
                    margin-top: 20px;
                    border-bottom: 2px solid #f3f4f6;
                    padding-bottom: 8px;
                    font-size: 1.1rem;
                }
                
                .expense-section {
                    background: #f9fafb;
                    padding: 15px;
                    border-radius: 8px;
                    margin-bottom: 15px;
                }
                
                .subsection-title {
                    color: #4b5563;
                    margin-bottom: 12px;
                    font-size: 0.95rem;
                    font-weight: 600;
                }
                
                .subsection-title.collapsible {
                    cursor: pointer;
                    user-select: none;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px;
                    background: #fff;
                    border-radius: 6px;
                    transition: background-color 0.2s;
                }
                
                .subsection-title.collapsible:hover {
                    background: #f3f4f6;
                }
                
                .toggle-icon {
                    font-size: 0.8rem;
                    color: #6b7280;
                }
                
                .grid-2-col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }
                
                @media (max-width: 768px) {
                    .grid-2-col {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

const InputGroup = ({ label, name, value, onChange, error }) => {
    const inputValue = value === null || value === undefined ? '' : String(value);

    return (
        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block', color: error ? '#dc2626' : '#374151' }}>
                {label}
            </label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                <input
                    type="number"
                    name={name}
                    value={inputValue}
                    onChange={onChange}
                    placeholder="0"
                    style={{
                        paddingLeft: '25px',
                        width: '100%',
                        padding: '8px 10px 8px 25px',
                        borderRadius: '6px',
                        border: error ? '1px solid #dc2626' : '1px solid #d1d5db'
                    }}
                    onWheel={(e) => {
                        e.target.blur();
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                />
            </div>
            {error && <span style={{ color: '#dc2626', fontSize: '0.8rem' }}>{error}</span>}
        </div>
    );
};

export default BusinessIncomeForm;
